import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BaseKeywordInterface } from '../common/interfaces/keyword/base-keyword.interface';
import { GetKeywordAPIInterface } from '../common/interfaces/keyword/get-keyword-API.interface';
import { selectKeywords } from '../common/interfaces/keyword-list/base-keyword-list.interface';
import { DriversIntegrationTypes } from '../projects/document-scan/drivers-integrations/drivers-integrations.types';
import { AiService } from '../services/ai.service';
import { AuthService } from '../services/auth.service';
import { ConversationKeywordSuggestion, KeywordsService } from '../services/keywords.service';
import { ToastV2Service } from '../services/toast-v2.service';
import { UploadManagerService } from '../services/upload-manager.service';

export interface ScanFromChatRequest {
  attachmentId: string;
  filename: string;
  /** Source conversation id — reserved for a future server-side inference path. */
  conversationId?: string;
  /** Plain conversation text (markers stripped) used to infer relevant keywords. */
  conversationText?: string;
}

/**
 * Orchestrates "Scan this document" from a NovaAI chat attachment (Phase 2).
 * Opens an in-chat keyword-review dialog seeded with:
 *   - M1: existing library keywords matched against the conversation (client-side),
 *   - M2b: NEW keyword suggestions from the backend (`/keywords/suggest-from-conversation`),
 * then reconstructs a File from the stored attachment bytes (creating any opted-in
 * suggestions in the library first) and hands it to the existing document-scan create
 * flow. Progress surfaces through the global <app-upload-overlay>, so no navigation here.
 *
 * See docs/NOVA_AI_SCAN_FROM_CHAT_DESIGN.md.
 */
@Injectable({ providedIn: 'root' })
export class ScanFromChatService {
  private readonly aiService = inject(AiService);
  private readonly authService = inject(AuthService);
  private readonly keywordsService = inject(KeywordsService);
  private readonly uploadManager = inject(UploadManagerService);
  private readonly toast = inject(ToastV2Service);

  readonly dialogVisible = signal(false);
  readonly request = signal<ScanFromChatRequest | null>(null);
  readonly initialKeywords = signal<selectKeywords[]>([]);
  readonly suggestions = signal<ConversationKeywordSuggestion[]>([]);
  readonly starting = signal(false);
  /** True while existing-library keyword inference (M1) is running. */
  readonly inferring = signal(false);
  /** True once an inference attempt with conversation text has completed. */
  readonly inferenceRan = signal(false);
  /** True while new-keyword suggestion (M2b) is running. */
  readonly suggesting = signal(false);

  open(request: ScanFromChatRequest): void {
    this.request.set(request);
    this.initialKeywords.set([]);
    this.suggestions.set([]);
    this.starting.set(false);
    this.inferenceRan.set(false);
    const hasText = !!request.conversationText?.trim();
    this.inferring.set(hasText);
    this.suggesting.set(hasText);
    this.dialogVisible.set(true);
    void this.inferKeywords(request);
    void this.loadSuggestions(request);
  }

  close(): void {
    this.dialogVisible.set(false);
    this.request.set(null);
    this.initialKeywords.set([]);
    this.suggestions.set([]);
    this.starting.set(false);
    this.inferring.set(false);
    this.inferenceRan.set(false);
    this.suggesting.set(false);
  }

  async startScan(
    selectedKeywords: selectKeywords[],
    acceptedSuggestions: ConversationKeywordSuggestion[] = []
  ): Promise<void> {
    const request = this.request();
    if (!request || this.starting()) return;

    if (selectedKeywords.length === 0 && acceptedSuggestions.length === 0) {
      this.toast.callErrorToast('Select at least one keyword to scan this document for.');
      return;
    }

    this.starting.set(true);
    try {
      const createdIds = await this.createSuggestedKeywords(acceptedSuggestions);
      const keywordIds = [...new Set([...selectedKeywords.map((keyword) => keyword.id), ...createdIds])];

      if (keywordIds.length === 0) {
        this.toast.callErrorToast('Could not prepare keywords for the scan. Please try again.');
        this.starting.set(false);
        return;
      }

      const blob = await lastValueFrom(this.aiService.getChatAttachmentBlob(request.attachmentId));
      const file = new File([blob], request.filename, {
        type: blob.type || this.guessMimeType(request.filename),
      });

      this.uploadManager.startFilesScanProject(
        [file],
        keywordIds,
        this.buildProjectName(request.filename),
        DriversIntegrationTypes.LOCAL_PICKER
      );

      this.close();
    } catch (error) {
      console.error('[SCAN_FROM_CHAT] Failed to start scan', error);
      this.toast.callErrorToast('Could not start the scan from this attachment. Please try again.');
      this.starting.set(false);
    }
  }

  /**
   * M1 inference (client-side): load the org keyword library and pre-select every
   * keyword whose name or a variance appears (as a whole word) in the conversation.
   * Non-fatal — on failure or no match the picker is left for manual selection.
   */
  private async inferKeywords(request: ScanFromChatRequest): Promise<void> {
    const text = request.conversationText?.trim();
    if (!text) return;

    try {
      const library = await lastValueFrom(this.keywordsService.getAllKeywords('', 'ALL'));
      if (this.request() !== request) return; // user closed/reopened meanwhile
      this.initialKeywords.set(this.matchLibraryToText(library, text));
    } catch (error) {
      console.error('[SCAN_FROM_CHAT] Keyword inference failed', error);
    } finally {
      if (this.request() === request) {
        this.inferenceRan.set(true);
        this.inferring.set(false);
      }
    }
  }

  /**
   * M2b suggestions (server-side): ask the backend for NEW keywords (not in the library)
   * grounded in the conversation. Non-fatal — on failure the suggestions section is empty.
   */
  private async loadSuggestions(request: ScanFromChatRequest): Promise<void> {
    const text = request.conversationText?.trim();
    if (!text) return;

    const organizationId = this.authService.getCurrentOrganization() ?? undefined;
    try {
      const response = await lastValueFrom(
        this.keywordsService.suggestKeywordsFromConversation(text, organizationId)
      );
      if (this.request() !== request) return;
      this.suggestions.set(response?.suggestions ?? []);
    } catch (error) {
      console.error('[SCAN_FROM_CHAT] Keyword suggestion failed', error);
    } finally {
      if (this.request() === request) {
        this.suggesting.set(false);
      }
    }
  }

  private async createSuggestedKeywords(suggestions: ConversationKeywordSuggestion[]): Promise<string[]> {
    if (!suggestions.length) return [];

    const companyId = this.authService.getCurrentOrganization() ?? '';
    const results = await Promise.allSettled(
      suggestions.map((suggestion) =>
        this.keywordsService.addNewKeyword(this.toKeywordPayload(suggestion, companyId))
      )
    );

    const ids = results
      .filter(
        (result): result is PromiseFulfilledResult<BaseKeywordInterface> =>
          result.status === 'fulfilled' && !!result.value?.id
      )
      .map((result) => result.value.id as string);

    const failed = results.length - ids.length;
    if (failed > 0) {
      this.toast.callErrorToast(`${failed} suggested keyword(s) couldn't be created and were skipped.`);
    }
    return ids;
  }

  private toKeywordPayload(
    suggestion: ConversationKeywordSuggestion,
    companyId: string
  ): BaseKeywordInterface {
    return {
      companyId,
      name: suggestion.name,
      riskRating: suggestion.riskRating,
      variances: [],
      keywordLists: [],
      exceptions: [],
      suggestions: [],
      notes: suggestion.reason ?? null,
      exactMatch: false,
    };
  }

  private matchLibraryToText(library: GetKeywordAPIInterface[], text: string): selectKeywords[] {
    const haystack = text.toLowerCase();
    return library
      .filter((keyword) => this.keywordAppearsIn(keyword, haystack))
      .map((keyword) => this.toSelectKeyword(keyword));
  }

  private keywordAppearsIn(keyword: GetKeywordAPIInterface, haystack: string): boolean {
    const terms = [keyword.name, ...(keyword.variances ?? [])]
      .map((term) => term?.trim().toLowerCase())
      .filter((term): term is string => !!term && term.length > 1);
    return terms.some((term) => this.containsWord(haystack, term));
  }

  /** Whole-word (ASCII-boundary) containment — avoids "cure" matching "secure". */
  private containsWord(haystack: string, needle: string): boolean {
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`).test(haystack);
  }

  private toSelectKeyword(keyword: GetKeywordAPIInterface): selectKeywords {
    return {
      id: keyword.id,
      name: keyword.name,
      selected: true,
      companyId: keyword.companyId,
      riskRating: keyword.riskRating,
      variances: keyword.variances ?? [],
      keywordLists: keyword.keywordLists ?? [],
      exceptions: keyword.exceptions ?? [],
      suggestions: keyword.suggestions ?? [],
      notes: keyword.notes ?? null,
      exactMatch: keyword.exactMatch ?? null,
    };
  }

  private buildProjectName(filename: string): string {
    const base = filename.replace(/\.[^.]+$/, '').trim();
    return base ? `Chat scan — ${base}` : 'Chat scan';
  }

  private guessMimeType(filename: string): string {
    return filename.toLowerCase().endsWith('.pdf')
      ? 'application/pdf'
      : 'application/octet-stream';
  }
}
